import React, { useEffect, useMemo, useState } from 'react';
import { CircleAlert, Clock, Megaphone, Search, Unlink, X } from 'lucide-react';
import { CashkaroService } from '../../../core/services/cashkaro.service';
import { StorefrontService } from '../../../core/services/storefront.service';

interface CampaignLinkRequest {
    id: number;
    cashkaro_campaign_id: number;
    cashkaro_campaign_title: string | null;
    cashkaro_campaign_thumbnail: string | null;
    status: 'pending' | 'approved' | 'rejected';
    reject_reason: string | null;
    requested_at: string;
}

interface Props {
    brand_id: number;
    storefront_id?: number;
    cashkaroCampaignId: number | null;
    cashkaroCampaignTitle: string | null;
    cashkaroCampaignThumbnail: string | null;
    latestCampaignRequest: CampaignLinkRequest | null;
    onLinked: () => void;
}

// Requests to link this storefront to one of the brand's own Affiliate Deals campaigns, so
// influencers viewing that campaign can pick a specific product (from this storefront)
// to promote instead of only the campaign's whole-site link.
//
// Trackier has no advertiser/ownership field to verify a campaign is really this
// brand's, so picking one doesn't link it immediately — it files a request
// (cashkaro_campaign_link_requests) that only takes effect once an internal admin
// approves it via the Publisher dashboard. Four states below: browse (no campaign, no
// pending request — every campaign is shown up front, with an optional filter, rather
// than requiring a search first) -> pending review -> live/linked -> rejected (can
// browse again).
export const StorefrontCashkaroLink: React.FC<Props> = ({
    brand_id, storefront_id, cashkaroCampaignId, cashkaroCampaignTitle, cashkaroCampaignThumbnail, latestCampaignRequest, onLinked
}) => {
    const [search, setSearch] = useState('');
    const [allCampaigns, setAllCampaigns] = useState<any[]>([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(false);
    const [saving, setSaving] = useState<number | null>(null); // trackier campaign_id currently being requested
    const [unlinking, setUnlinking] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

    const isPending = !cashkaroCampaignId && latestCampaignRequest?.status === 'pending';
    const isRejected = !cashkaroCampaignId && latestCampaignRequest?.status === 'rejected';
    const canBrowse = !cashkaroCampaignId && !isPending;

    useEffect(() => {
        if (!canBrowse || !storefront_id) { setAllCampaigns([]); return; }
        setLoadingCampaigns(true);
        CashkaroService.searchCampaignsForBrand({ brand_id, page_size: 100 })
            .then((res: any) => setAllCampaigns(res?.data?.campaigns || []))
            .catch(() => setAllCampaigns([]))
            .finally(() => setLoadingCampaigns(false));
    }, [brand_id, storefront_id, canBrowse]);

    const results = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return allCampaigns;
        return allCampaigns.filter((c) => (c.title || '').toLowerCase().includes(q));
    }, [allCampaigns, search]);

    const requestCampaign = async (campaign_id: number) => {
        if (!storefront_id) return;
        setSaving(campaign_id);
        setAlert(null);
        try {
            const res = await StorefrontService.requestCashkaroCampaignLink({ storefront_id, brand_id, cashkaro_campaign_id: campaign_id });
            if (res?.status === 1) {
                setSearch('');
                onLinked();
            } else {
                setAlert({ type: 'error', msg: res?.info || 'Failed to submit request' });
            }
        } catch {
            setAlert({ type: 'error', msg: 'Something went wrong' });
        } finally {
            setSaving(null);
        }
    };

    const unlinkCampaign = async () => {
        if (!storefront_id) return;
        if (!window.confirm('Unlink this Affiliate Deals campaign from your storefront? Influencers will no longer see your storefront products on it.')) return;
        setUnlinking(true);
        setAlert(null);
        try {
            const res = await StorefrontService.unlinkCashkaroCampaign({ storefront_id, brand_id });
            if (res?.status === 1) onLinked();
            else setAlert({ type: 'error', msg: res?.info || 'Failed to unlink campaign' });
        } catch {
            setAlert({ type: 'error', msg: 'Something went wrong' });
        } finally {
            setUnlinking(false);
        }
    };

    const thumb = (src: string | null | undefined) =>
        src ? <img src={src} alt="" className="brd-store-table__thumb" /> : <span className="brd-store-table__thumb"><Megaphone size={16} /></span>;

    return (
        <section className="brd-section">
            <h3 className="brd-section__title brd-store-ck__title"><Megaphone size={18} /> Affiliate Deals Campaign</h3>
            <p className="brd-field__hint">
                Link this storefront to your Affiliate Deals campaign so influencers can pick a specific
                product to promote, instead of only getting one link for your whole site. An admin reviews and
                approves each request before it goes live.
            </p>

            {alert && (
                <div className={`brd-banner ${alert.type === 'error' ? 'brd-banner--error' : 'brd-banner--info'}`} role="status">
                    <CircleAlert size={16} /><span style={{ flex: 1 }}>{alert.msg}</span>
                    <button type="button" onClick={() => setAlert(null)} aria-label="Dismiss"><X size={14} /></button>
                </div>
            )}

            {cashkaroCampaignId ? (
                <div className="brd-store-ck__row">
                    {thumb(cashkaroCampaignThumbnail)}
                    <div className="brd-store-ck__text">
                        <p className="brd-store-table__title">{cashkaroCampaignTitle || `Campaign #${cashkaroCampaignId}`}</p>
                        <span className="brd-pill brd-pill--success">Currently linked</span>
                    </div>
                    <button type="button" className="brd-btn brd-btn--danger-outline brd-btn--sm" onClick={unlinkCampaign} disabled={unlinking}>
                        {unlinking ? <span className="brd-spinner brd-spinner--sm" /> : <Unlink size={14} />} Unlink
                    </button>
                </div>
            ) : isPending ? (
                <div className="brd-store-ck__row brd-store-ck__row--pending">
                    {thumb(latestCampaignRequest?.cashkaro_campaign_thumbnail)}
                    <div className="brd-store-ck__text">
                        <p className="brd-store-table__title">
                            {latestCampaignRequest?.cashkaro_campaign_title || `Campaign #${latestCampaignRequest?.cashkaro_campaign_id}`}
                        </p>
                        <span className="brd-store-table__muted">
                            Requested {latestCampaignRequest?.requested_at ? new Date(latestCampaignRequest.requested_at).toLocaleDateString() : ''}
                        </span>
                    </div>
                    <span className="brd-pill brd-pill--warning"><Clock size={12} /> Pending admin review</span>
                </div>
            ) : (
                <div>
                    {isRejected && (
                        <div className="brd-banner brd-banner--error">
                            <CircleAlert size={16} />
                            <span>
                                Your request to link "{latestCampaignRequest?.cashkaro_campaign_title || `Campaign #${latestCampaignRequest?.cashkaro_campaign_id}`}"
                                was rejected{latestCampaignRequest?.reject_reason ? `: ${latestCampaignRequest.reject_reason}` : '.'} Pick a different campaign below.
                            </span>
                        </div>
                    )}
                    <label className="brd-search">
                        <Search size={16} />
                        <input placeholder="Filter campaigns by name…" value={search} onChange={e => setSearch(e.target.value)}
                            disabled={!storefront_id} aria-label="Filter campaigns" />
                        {loadingCampaigns && <span className="brd-spinner brd-spinner--sm" />}
                    </label>
                    {!storefront_id && <p className="brd-store__hint">Save your storefront details first</p>}
                    {loadingCampaigns ? (
                        <div className="brd-center" style={{ padding: 24 }}><span className="brd-spinner" /></div>
                    ) : results.length > 0 ? (
                        <ul className="brd-store-ck__list">
                            {results.map((c: any) => (
                                <li key={c.trackier_id}>
                                    <button type="button" className="brd-store-ck__item" onClick={() => requestCampaign(c.trackier_id)} disabled={saving !== null}>
                                        {thumb(c.thumbnail)}
                                        <span className="brd-store-ck__text brd-store-table__title">{c.title}</span>
                                        {saving === c.trackier_id ? <span className="brd-spinner brd-spinner--sm" /> : <span className="brd-link">Request</span>}
                                    </button>
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p className="brd-store__hint">{search.trim() ? 'No matching campaigns found.' : 'No campaigns available.'}</p>
                    )}
                </div>
            )}
        </section>
    );
};
